/**
 * Mapa de calor — issue #43.
 *
 * Consume `GET /api/v1/analytics/heatmap`, que devuelve **celdas de geohash ya
 * agregadas**. Nunca eventos: además de que cinco millones de puntos no se
 * dibujan punto por punto, un evento de escaneo tiene ubicación, momento y
 * producto, y en farma esa terna permite inferir la condición de salud de una
 * persona. El tenant ve agregados (regla 5 del dominio), y la barrera está en
 * el servidor —el endpoint lee `analytics_geo_cell`, no `scan_event`— así que
 * este cliente no tiene forma de pedir un individuo aunque se lo propusiera.
 *
 * Cómo se cumple "cambiar de filtro no recarga el mapa entero"
 * -----------------------------------------------------------
 * `HeatmapCanvas` se monta una sola vez y nunca se desmonta al cambiar un
 * filtro; sólo cambian las capas de deck.gl. Y mientras la consulta nueva está
 * en vuelo se siguen mostrando las celdas anteriores atenuadas, en vez de
 * vaciar el mapa: un mapa en blanco durante 300 ms se lee como una falla, y
 * además hace perder la referencia visual de qué cambió.
 *
 * La capa de alertas es una **segunda consulta de celdas** (`layer=suspicious`),
 * no un filtro del lado del cliente. Tiene que ser así: en la respuesta de una
 * capa concreta el campo `suspicious` sólo cuenta lo que esa capa ya incluye,
 * de modo que derivarla del mismo payload daría cero sobre `layer=genuine`.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import { analyticsKeys } from '@/api/keys';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { CellTable } from './CellTable';
import {
  byScansDesc,
  cellStats,
  legendStops,
  type HeatCell,
  type HeatmapResponse,
} from './cells';
import {
  DEFAULT_MAP_FILTERS,
  clampFilters,
  heatmapKeyOf,
  isAlertLayer,
  toHeatmapQuery,
  type MapFilters,
} from './filters';
import { MapFiltersBar } from './MapFilters';

// MapLibre y deck.gl son ~1,2 MB: viajan en su propio chunk y sólo los baja
// quien abre el mapa. El resto de la vista —filtros, tabla, estados— no
// espera a ese chunk y se dibuja de entrada.
const HeatmapCanvas = lazy(() =>
  import('./HeatmapCanvas').then((mod) => ({ default: mod.HeatmapCanvas })),
);

const HEATMAP_PATH = '/api/v1/analytics/heatmap';
/** Las celdas cambian sólo cuando corre el rollup: no vale la pena repetirlas. */
const STALE_MS = 60_000;
/** Filas de tabla que se pintan de entrada. Cinco mil `<tr>` sí trabarían. */
const TABLE_PAGE = 50;

const EMPTY_CELLS: readonly HeatCell[] = [];

export function MapPage(): ReactNode {
  const { t, fmt } = useI18n();
  const [filters, setFilters] = useState<MapFilters>(DEFAULT_MAP_FILTERS);
  const [selected, setSelected] = useState<HeatCell | null>(null);
  const [tableRows, setTableRows] = useState(TABLE_PAGE);
  const [fitToken, setFitToken] = useState(0);

  const baseKey = useMemo(() => heatmapKeyOf(filters), [filters]);
  const base = useApiQuery<HeatmapResponse>(
    analyticsKeys.heatmap(baseKey),
    HEATMAP_PATH,
    toHeatmapQuery(baseKey),
    { staleTime: STALE_MS },
  );

  // La capa de alertas comparte período, precisión y umbral con la base: si no,
  // se estarían superponiendo dos mapas de escalas distintas.
  const alertKey = useMemo(() => ({ ...baseKey, layer: 'suspicious' }), [baseKey]);
  const overlayWanted = filters.alertOverlay && !isAlertLayer(filters.layer);
  const alerts = useApiQuery<HeatmapResponse>(
    analyticsKeys.heatmap(alertKey),
    HEATMAP_PATH,
    toHeatmapQuery(alertKey),
    { staleTime: STALE_MS, enabled: overlayWanted },
  );

  // Última respuesta buena, para no vaciar el mapa mientras entra la
  // siguiente. Cuando cambia un filtro, `base.data` pasa a `undefined` en el
  // mismo render pero este estado todavía tiene la respuesta anterior, así que
  // el mapa nunca queda en blanco: se atenúa y se redibuja cuando llega el
  // dato nuevo.
  const [lastGood, setLastGood] = useState<HeatmapResponse | null>(null);
  // Ajuste de estado durante el render (el patrón que React documenta para
  // "derivar de props que cambiaron"). No va en un `useEffect`: ahí el mapa se
  // pintaría una vez con el dato viejo ya habiendo llegado el nuevo, y el
  // efecto dispararía un segundo render en cascada por cada respuesta.
  if (base.data !== undefined && base.data !== lastGood) setLastGood(base.data);
  const shown = base.data ?? lastGood;

  const cells = shown?.cells ?? EMPTY_CELLS;
  const alertCells = overlayWanted ? (alerts.data?.cells ?? EMPTY_CELLS) : EMPTY_CELLS;
  const stats = useMemo(() => cellStats(cells), [cells]);
  const ranked = useMemo(() => byScansDesc(cells), [cells]);
  const legend = useMemo(() => legendStops(stats.maxScans), [stats.maxScans]);

  // Encuadre automático una única vez, cuando llega el primer dato. Repetirlo
  // en cada respuesta le movería el mapa al usuario mientras mira una zona.
  const fitted = useRef(false);
  useEffect(() => {
    if (!fitted.current && cells.length > 0) {
      fitted.current = true;
      setFitToken((token) => token + 1);
    }
  }, [cells.length]);

  const describeCell = useCallback(
    (cell: HeatCell) =>
      [
        `${t('map.table.scans')}: ${fmt.number(cell.scans)}`,
        `${t('map.table.suspicious')}: ${fmt.number(cell.suspicious)}`,
        `${t('map.table.peakUnits')}: ${fmt.number(cell.peak_daily_units)}`,
        cell.country_code ?? '',
      ]
        .filter((line) => line !== '')
        .join('\n'),
    [t, fmt],
  );

  const changeFilters = useCallback((next: MapFilters) => {
    setFilters(clampFilters(next));
    setTableRows(TABLE_PAGE);
    setSelected(null);
  }, []);

  const busy = base.isFetching || (overlayWanted && alerts.isFetching);
  const firstLoad = base.isPending && lastGood === null;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <Card>
        <CardHeader
          title={t('map.title')}
          description={t('map.description')}
          actions={
            <Badge tone={busy ? 'progress' : 'neutral'}>
              {busy ? t('state.refreshing') : t('map.cells', { count: fmt.number(stats.cells) })}
            </Badge>
          }
        />
        <CardBody className="border-b border-border">
          <MapFiltersBar value={filters} onChange={changeFilters} disabled={firstLoad} />
        </CardBody>

        {base.isError && (
          <CardBody>
            <ErrorState
              error={base.error}
              onRetry={() => {
                void base.refetch();
              }}
            />
          </CardBody>
        )}

        {/* `firstLoad` ya implica `isPending`, y el estado de TanStack es
            excluyente: no hace falta descartar el error otra vez. */}
        {firstLoad && <LoadingState rows={6} label={t('map.loading')} />}

        {!firstLoad && !base.isError && (
          <>
            <CardBody className="flex flex-col gap-3">
              <Suspense fallback={<LoadingState rows={6} label={t('map.loading')} />}>
                <HeatmapCanvas
                  cells={cells}
                  alertCells={alertCells}
                  fitToken={fitToken}
                  focusCell={selected}
                  stale={busy}
                  describeCell={describeCell}
                  onSelectCell={setSelected}
                  label={t('map.canvas.label')}
                />
              </Suspense>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-ink-muted">{t('map.legend.title')}</span>
                  <div className="flex items-center gap-1">
                    {legend.map((stop) => (
                      <span
                        key={stop.intensity}
                        className="flex flex-col items-center gap-0.5 text-2xs text-ink-subtle"
                      >
                        <span
                          aria-hidden="true"
                          className="block h-3 w-8 rounded-sm"
                          style={{
                            backgroundColor: `rgb(${stop.color[0]} ${stop.color[1]} ${stop.color[2]})`,
                          }}
                        />
                        {fmt.compact(stop.scans)}
                      </span>
                    ))}
                  </div>
                  {alertCells.length > 0 && (
                    <span className="ml-3 flex items-center gap-1 text-2xs text-ink-subtle">
                      <span
                        aria-hidden="true"
                        className="block size-3 rounded-sm border-2 border-critical"
                      />
                      {t('map.legend.alerts')}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setFitToken((token) => token + 1);
                  }}
                >
                  {t('map.recenter')}
                </Button>
              </div>
            </CardBody>

            <CardBody className="grid gap-3 border-t border-border text-sm sm:grid-cols-2 lg:grid-cols-4">
              <Stat label={t('map.stat.scans')} value={fmt.number(shown?.total_scans ?? 0)} />
              <Stat label={t('map.stat.cells')} value={fmt.number(stats.cells)} />
              <Stat
                label={t('map.stat.suspicious')}
                value={fmt.number(alertCells.length > 0 ? cellStats(alertCells).scans : stats.suspicious)}
              />
              <Stat
                label={t('map.stat.cellSize')}
                value={t('map.stat.cellSizeValue', {
                  width: fmt.number((shown?.cell_width_m ?? 0) / 1000, 1),
                  height: fmt.number((shown?.cell_height_m ?? 0) / 1000, 1),
                })}
              />
            </CardBody>

            <CardBody className="border-t border-border text-xs text-ink-subtle">
              <p>{shown?.suppressed_note ?? ''}</p>
              <p className="mt-1">
                {t('map.footer.window', {
                  from: fmt.date(shown?.from_day),
                  to: fmt.date(shown?.to_day),
                })}
                {' · '}
                {t('map.footer.refreshed', {
                  when:
                    shown?.data_refreshed_at === null || shown?.data_refreshed_at === undefined
                      ? '—'
                      : fmt.dateTime(shown.data_refreshed_at),
                })}
                {' · '}
                {t('map.footer.queryMs', { ms: fmt.number(shown?.query_ms ?? 0, 0) })}
              </p>
              {shown?.truncated === true && (
                <p className="mt-2 text-suspect">{t('map.truncated')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>

      <Card>
        <CardHeader
          title={t('map.table.title')}
          description={t('map.table.description')}
          actions={
            selected !== null ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelected(null);
                }}
              >
                {t('map.table.clearSelection')}
              </Button>
            ) : undefined
          }
        />
        {ranked.length === 0 ? (
          <EmptyState title={t('map.empty')} detail={t('map.empty.detail')} />
        ) : (
          <>
            <CellTable
              cells={ranked.slice(0, tableRows)}
              totalScans={stats.scans}
              selectedCell={selected?.cell ?? null}
              onSelect={setSelected}
            />
            <CardBody className="flex items-center justify-center border-t border-border">
              {tableRows < ranked.length ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setTableRows((rows) => rows + TABLE_PAGE * 4);
                  }}
                >
                  {t('map.table.more', {
                    shown: fmt.number(tableRows),
                    total: fmt.number(ranked.length),
                  })}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <p className="text-xs text-ink-muted">{label}</p>
      <p className="text-lg font-semibold tabular-nums text-ink">{value}</p>
    </div>
  );
}
